import { StyleSheet } from '@react-pdf/renderer';

/**
 * Shared PDF styles for all Hail Mary production documents.
 * Uses a consistent brand palette: navy headings, warm gold accents, clean layout.
 */

// Brand colours
export const BRAND = {
  navy: '#0f172a',
  slate700: '#334155',
  slate500: '#64748b',
  slate300: '#cbd5e1',
  slate100: '#f1f5f9',
  amber500: '#f59e0b',
  amber100: '#fef3c7',
  white: '#ffffff',
  green600: '#16a34a',
};

export const baseStyles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 60,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: BRAND.slate700,
  },
  // Header
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    borderBottomWidth: 2,
    borderBottomColor: BRAND.navy,
    paddingBottom: 12,
  },
  brandName: {
    fontSize: 18,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.navy,
  },
  docTitle: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.amber500,
    textAlign: 'right',
  },
  metaText: {
    fontSize: 8,
    color: BRAND.slate500,
    textAlign: 'right',
    marginTop: 2,
  },
  // Sections
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.navy,
    marginBottom: 8,
    marginTop: 16,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.slate300,
  },
  // Key-Value rows
  kvRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
  },
  kvLabel: {
    fontSize: 9,
    color: BRAND.slate500,
    width: '50%',
  },
  kvValue: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.slate700,
    width: '50%',
    textAlign: 'right',
  },
  // Table
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: BRAND.navy,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 2,
  },
  tableHeaderCell: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.white,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: BRAND.slate300,
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: BRAND.slate300,
    backgroundColor: BRAND.slate100,
  },
  tableCell: {
    fontSize: 9,
    color: BRAND.slate700,
  },
  // Totals
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 8,
    marginTop: 8,
    backgroundColor: BRAND.navy,
    borderRadius: 2,
  },
  totalLabel: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.white,
  },
  totalValue: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: BRAND.amber500,
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 7,
    color: BRAND.slate500,
    borderTopWidth: 0.5,
    borderTopColor: BRAND.slate300,
    paddingTop: 8,
  },
  // Notes
  noteBox: {
    marginTop: 12,
    padding: 10,
    backgroundColor: BRAND.amber100,
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: BRAND.amber500,
  },
  noteText: {
    fontSize: 8,
    color: BRAND.slate700,
    lineHeight: 1.4,
  },
});

/** Format millimeters as a readable string */
export function fmtMm(mm: number): string {
  return `${mm.toLocaleString()} mm`;
}

/** Format meters to 2dp */
export function fmtM(m: number): string {
  return `${m.toFixed(2)} m`;
}

/** Format currency */
export function fmtCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Generate a human-readable order number */
export function generateOrderNumber(): string {
  const now = new Date();
  const datePart = now.toISOString().slice(0, 10).replace(/-/g, '');
  const rand = Math.floor(Math.random() * 9000 + 1000);
  return `HM-${datePart}-${rand}`;
}
